import { __assign, __extends, __rest } from "tslib";
import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { transitions } from '@sinoui/theme';
import { getDuration, getEasing } from './transitionUtils';
/**
 * 创建通过css样式定义过渡效果的过渡组件。
 *
 * 元素入场过渡： 先应用`enterStyle`，一帧（16ms）之后，应用`enterActiveStyle`和`enterToStyle`。
 *
 * 元素出场过渡： 先应用`exitStyle`，一帧（16ms）之后，应用`exitActiveStyle`和`enterToStyle`。
 *
 */
export default function createStyleTransition(options, defaultProps) {
    var _a;
    return _a = /** @class */ (function (_super) {
            __extends(StyleTransition, _super);
            function StyleTransition(props) {
                var _this = _super.call(this, props) || this;
                _this.handleEnter = _this.handleEnter.bind(_this);
                _this.handleEntering = _this.handleEntering.bind(_this);
                _this.handleExit = _this.handleExit.bind(_this);
                _this.handleExiting = _this.handleExiting.bind(_this);
                _this.handleTransitionEnd = _this.handleTransitionEnd.bind(_this);
                return _this;
            }
            Object.defineProperty(StyleTransition.prototype, "easing", {
                get: function () {
                    return getEasing(this.props.easing, options.easing);
                },
                enumerable: true,
                configurable: true
            });
            StyleTransition.prototype.getTimeout = function (node) {
                var timeout = this.props.timeout || options.duration;
                if (timeout === 'auto') {
                    var duration = transitions.getAutoHeightDuration(node.clientHeight);
                    return {
                        enter: duration,
                        exit: duration,
                    };
                }
                return getDuration(timeout, options.duration);
            };
            /**
             * 处理过渡的enter事件
             *
             * @param {HTMLElement} node 应用过渡效果的节点
             * @param {boolean} isAppearing 表示是否是刚创建节点时机。
             *  如果`HorizontalSlide`组件设置了`appear`属性，则会在刚创建节点时，
             *  调用此方法，且isAppearing为true；否则不会调用此方法。
             */
            StyleTransition.prototype.handleEnter = function (node, isAppearing) {
                if (this.props.onEnter) {
                    this.props.onEnter(node, isAppearing);
                }
                var enterStyle = __assign({ transition: 'none' }, options.enterStyle(this, node, isAppearing));
                Object.assign(node.style, enterStyle);
            };
            /**
             * 处理过渡的entering事件
             *
             * @param {HTMLElement} node 应用过渡效果的节点
             * @param {boolean} isAppearing 表示是否是刚创建节点时机。
             *  如果`HorizontalSlide`组件设置了`appear`属性，则会在刚创建节点时，
             *  调用此方法，且isAppearing为true；否则不会调用此方法。
             */
            StyleTransition.prototype.handleEntering = function (node, isAppearing) {
                var activeStyle = options.enterActiveStyle
                    ? options.enterActiveStyle(this, node, isAppearing)
                    : {};
                if (!activeStyle.transition) {
                    activeStyle.transition = transitions.create(options.transitionProps, {
                        duration: this.getTimeout(node).enter,
                        easing: this.easing.enter,
                    });
                }
                var enterActiveStyle = __assign(__assign({}, activeStyle), options.enterToStyle(this, node, isAppearing));
                Object.assign(node.style, enterActiveStyle);
                if (this.props.onEntering) {
                    this.props.onEntering(node, isAppearing);
                }
            };
            /**
             * 处理过渡的exit事件
             *
             * @param {HTMLElement} node 应用过渡效果的节点
             * @param {boolean} isAppearing 表示是否是刚创建节点时机。
             *  如果`HorizontalSlide`组件设置了`appear`属性，则会在刚创建节点时，
             *  调用此方法，且isAppearing为true；否则不会调用此方法。
             */
            StyleTransition.prototype.handleExit = function (node) {
                var exitStyle = __assign({ transition: 'none' }, options.exitStyle(this, node));
                Object.assign(node.style, exitStyle);
                if (this.props.onExit) {
                    this.props.onExit(node);
                }
            };
            /**
             * 处理过渡的exiting事件
             *
             * @param {HTMLElement} node 应用过渡效果的节点
             */
            StyleTransition.prototype.handleExiting = function (node) {
                var exitActiveStyle = options.exitActiveStyle
                    ? options.exitActiveStyle(this, node)
                    : {};
                if (!exitActiveStyle.transition) {
                    exitActiveStyle.transition = transitions.create(options.transitionProps, {
                        duration: this.getTimeout(node).exit,
                        easing: this.easing.exit,
                    });
                }
                var style = __assign(__assign({}, exitActiveStyle), options.exitToStyle(this, node));
                Object.assign(node.style, style);
                if (this.props.onExiting) {
                    this.props.onExiting(node);
                }
            };
            /**
             * 过渡结束监听器。
             *
             * @param {HTMLElement} node 过渡元素
             * @param {() => void} next 通知`Transition`组件过渡结束的方法。
             */
            StyleTransition.prototype.handleTransitionEnd = function (node, next) {
                if (this.props.timeout === 'auto') {
                    setTimeout(next, this.getTimeout(node).enter);
                }
            };
            StyleTransition.prototype.render = function () {
                var _a = this
                    .props, transitionClasses = _a.transitionClasses, direction = _a.direction, timeout = _a.timeout, props = __rest(_a, ["transitionClasses", "direction", "timeout"]);
                return (React.createElement(CSSTransition, __assign({}, props, { onEnter: this.handleEnter, onEntering: this.handleEntering, onExit: this.handleExit, onExiting: this.handleExiting, timeout: timeout === 'auto' ? null : getDuration(timeout, options.duration), classNames: transitionClasses || '', addEndListener: this.handleTransitionEnd })));
            };
            return StyleTransition;
        }(React.Component)),
        _a.defaultProps = defaultProps,
        _a;
}
//# sourceMappingURL=createStyleTransition.js.map